import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { FACILITIES } from '@/constants/testIds';
import { Plus, HouseLine, ArrowRight, DotsThreeVertical, PencilSimple, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ConfirmDialog from '@/components/ConfirmDialog';
import { HouseholdName } from '@/components/Present';
import { toast } from 'sonner';

const KINDS = ['household', 'clinic', 'senior community', 'assisted living', 'other'];

function HouseholdForm({ initial, onSaved, submitLabel = 'Add household' }) {
    const [name, setName] = useState(initial?.name || '');
    const [kind, setKind] = useState(initial?.kind || 'household');
    const [busy, setBusy] = useState(false);
    const save = async () => {
        if (!name.trim()) { toast.error('Name required'); return; }
        setBusy(true);
        try {
            const method = initial ? 'patch' : 'post';
            const url = initial ? `/facilities/${initial.facility_id}` : '/facilities';
            await api[method](url, { name, kind });
            toast.success('Saved');
            onSaved?.();
        } catch { toast.error('Failed'); }
        finally { setBusy(false); }
    };
    return (
        <>
            <div className="space-y-3">
                <div>
                    <Label>Household name</Label>
                    <Input data-testid={FACILITIES.nameInput} value={name} onChange={e => setName(e.target.value)} placeholder='e.g., "Mom & Dad" or "The Reyes Family"' />
                </div>
                <div>
                    <Label>Kind</Label>
                    <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger data-testid={FACILITIES.kindInput}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <DialogFooter>
                <Button data-testid={FACILITIES.submit} disabled={busy} onClick={save} className="rounded-full bg-primary text-primary-foreground">
                    {busy ? 'Saving…' : submitLabel}
                </Button>
            </DialogFooter>
        </>
    );
}

export default function Households() {
    const qc = useQueryClient();
    const { data: facilities = [], isLoading } = useQuery({
        queryKey: ['facilities'],
        queryFn: async () => (await api.get('/facilities')).data,
    });

    const [addOpen, setAddOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [del, setDel] = useState(null);

    const invalidate = () => qc.invalidateQueries({ queryKey: ['facilities'] });
    const confirmDelete = async () => {
        try {
            await api.delete(`/facilities/${del.facility_id}`);
            toast.success('Household removed');
            setDel(null); invalidate();
        } catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={FACILITIES.root} className="px-6 md:px-10 py-8 max-w-7xl">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Your people</div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Households</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">Little groups of people you look after together — a household, a clinic, a senior community.</p>
                </div>
                <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={FACILITIES.addButton} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                            <Plus size={16} className="mr-1" /> Add household
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading text-2xl">New household</DialogTitle></DialogHeader>
                        <HouseholdForm onSaved={() => { setAddOpen(false); invalidate(); }} />
                    </DialogContent>
                </Dialog>
            </header>

            {isLoading ? (
                <div className="text-slate-500 text-sm">Loading…</div>
            ) : facilities.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center">
                    <HouseLine size={40} className="mx-auto text-slate-400" />
                    <div className="font-heading text-2xl mt-3 text-slate-800">Add your first household to get started</div>
                    <p className="text-slate-500 text-sm mt-1">A household groups the family members you look after together.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {facilities.map(f => (
                        <div key={f.facility_id} data-testid={FACILITIES.card} className="group rounded-xl border border-slate-200 bg-card p-5 shadow-sm hover:border-primary transition-colors duration-150 relative">
                            <div className="absolute top-3 right-3">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" aria-label="More">
                                            <DotsThreeVertical size={18} weight="bold" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem data-testid={FACILITIES.editButton} onClick={() => setEdit(f)}>
                                            <PencilSimple size={14} className="mr-2" /> Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem data-testid={FACILITIES.deleteButton} onClick={() => setDel(f)} className="text-red-600 focus:text-red-700">
                                            <Trash size={14} className="mr-2" /> Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <Link to={`/households/${f.facility_id}`} className="block">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
                                    <span>{f.kind}</span>
                                    {f.is_sample && (
                                        <span data-testid="household-sample-chip" className="text-[9px] uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Sample data</span>
                                    )}
                                </div>
                                <div className="font-heading text-2xl mt-1 text-slate-900 pr-8"><HouseholdName name={f.name} /></div>
                                <div className="mt-6 flex items-center justify-between text-sm">
                                    <span className="text-slate-600">{f.patient_count} member{f.patient_count === 1 ? '' : 's'}</span>
                                    <span data-testid={FACILITIES.openLink} className="inline-flex items-center gap-1 text-primary group-hover:translate-x-0.5 transition-transform duration-150">
                                        Open <ArrowRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit household</DialogTitle></DialogHeader>
                    {edit && <HouseholdForm initial={edit} submitLabel="Save" onSaved={() => { setEdit(null); invalidate(); }} />}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!del}
                onOpenChange={(v) => !v && setDel(null)}
                title="Delete this household?"
                description={del ? `This removes “${del.name}” and everyone in it — appointments, medications, lab results, and notes. This can't be undone.` : ''}
                onConfirm={confirmDelete}
                confirmLabel="Yes, delete household"
            />
        </div>
    );
}
